import { create } from 'zustand';

/**
 * Shift store — drives the shift-open and shift-close fullscreen takeovers.
 *
 * Denominations match the prototype: SAR notes/coins.
 *
 * When feature 3 (Terminals + Shifts) lands on the backend, this store calls
 * shiftAPI.open() / shiftAPI.close() instead of staying purely local.
 */

export const DENOMINATIONS = [
  { value: 500, label: '500', kind: 'note' as const },
  { value: 200, label: '200', kind: 'note' as const },
  { value: 100, label: '100', kind: 'note' as const },
  { value: 50, label: '50', kind: 'note' as const },
  { value: 20, label: '20', kind: 'note' as const },
  { value: 10, label: '10', kind: 'note' as const },
  { value: 5, label: '5', kind: 'note' as const },
  { value: 2, label: '2', kind: 'coin' as const },
  { value: 1, label: '1', kind: 'coin' as const },
  { value: 0.5, label: '0.50', kind: 'coin' as const },
  { value: 0.25, label: '0.25', kind: 'coin' as const },
];

export type CountMode = 'denom' | 'single';

interface ShiftState {
  // Open shift
  openMode: CountMode;
  /** denominationKey (string) -> count */
  openCounts: Record<string, number>;
  openSingleAmount: number;
  openNotes: string;

  // Close shift
  closeMode: CountMode;
  closeCounts: Record<string, number>;
  closeSingleAmount: number;
  closeNotes: string;

  // Shift info (set when shift opens)
  shiftId: string | null;
  shiftCode: string;
  openedAt: Date | null;
  openingCash: number;

  // Computed totals (live from counts)
  openTotal: () => number;
  closeTotal: () => number;

  // Actions
  setOpenMode: (m: CountMode) => void;
  setOpenCount: (denom: string, count: number) => void;
  setOpenSingleAmount: (amt: number) => void;
  setOpenNotes: (s: string) => void;
  confirmOpenShift: () => void;
  resetOpen: () => void;

  setCloseMode: (m: CountMode) => void;
  setCloseCount: (denom: string, count: number) => void;
  setCloseSingleAmount: (amt: number) => void;
  setCloseNotes: (s: string) => void;
  confirmCloseShift: () => void;
  resetClose: () => void;
}

export const useShift = create<ShiftState>((set, get) => ({
  openMode: 'denom',
  openCounts: {},
  openSingleAmount: 0,
  openNotes: '',

  // Closing defaults to a typed total — most drawers get counted once rather
  // than sorted into notes. Denomination counting stays one click away.
  closeMode: 'single',
  closeCounts: {},
  closeSingleAmount: 0,
  closeNotes: '',

  shiftId: null,
  shiftCode: '',
  openedAt: null,
  openingCash: 0,

  openTotal: () => {
    const s = get();
    if (s.openMode === 'single') return s.openSingleAmount;
    return DENOMINATIONS.reduce((a, d) => a + d.value * (s.openCounts[String(d.value)] ?? 0), 0);
  },

  closeTotal: () => {
    const s = get();
    if (s.closeMode === 'single') return s.closeSingleAmount;
    return DENOMINATIONS.reduce((a, d) => a + d.value * (s.closeCounts[String(d.value)] ?? 0), 0);
  },

  setOpenMode: (m) => set({ openMode: m }),
  setOpenCount: (denom, count) =>
    set((s) => ({ openCounts: { ...s.openCounts, [denom]: Math.max(0, count) } })),
  setOpenSingleAmount: (n) => set({ openSingleAmount: Math.max(0, n) }),
  setOpenNotes: (s) => set({ openNotes: s }),

  confirmOpenShift: () => {
    const total = get().openTotal();
    const code = `Z-${String(Date.now()).slice(-4)}`;
    set({
      shiftId: code,
      shiftCode: code,
      openedAt: new Date(),
      openingCash: total,
    });
  },

  resetOpen: () =>
    set({ openMode: 'denom', openCounts: {}, openSingleAmount: 0, openNotes: '' }),

  setCloseMode: (m) => set({ closeMode: m }),
  setCloseCount: (denom, count) =>
    set((s) => ({ closeCounts: { ...s.closeCounts, [denom]: Math.max(0, count) } })),
  setCloseSingleAmount: (n) => set({ closeSingleAmount: Math.max(0, n) }),
  setCloseNotes: (s) => set({ closeNotes: s }),

  confirmCloseShift: () => {
    set({
      shiftId: null,
      shiftCode: '',
      openedAt: null,
      openingCash: 0,
    });
  },

  resetClose: () =>
    set({ closeMode: 'single', closeCounts: {}, closeSingleAmount: 0, closeNotes: '' }),
}));
